"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Poppins } from "next/font/google";
import { useSession, signOut } from "next-auth/react";
import { Bars3Icon, XMarkIcon, UserCircleIcon } from "@heroicons/react/24/outline";

const poppinsFont = Poppins({
  subsets: ["latin"],
  weight: ["600"],
});

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { data: session } = useSession();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-full z-50 transition-all duration-300",
        scrolled
          ? "bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-100"
          : "bg-white/90 backdrop-blur-sm shadow-sm"
      )}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
        {/* Logo */}
        <div className="flex items-center">
          <Link href="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden relative">
              <Image 
                src="/images/logo.png" 
                alt="OBS-SAVET.EU Logo" 
                fill
                className="object-contain"
                sizes="40px"
                priority
              />
            </div>
            <div>
              <h1 className={cn(
                "text-xl font-bold transition-colors duration-300",
                scrolled ? "text-gray-900" : "text-gray-900",
                poppinsFont.className
              )}>
                OBS-SAVET.EU
              </h1>
              <span className={cn(
                "text-xs font-medium transition-colors duration-300",
                scrolled ? "text-gray-600" : "text-gray-700"
              )}>
                уеб портал за общински съвети
              </span>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link
            href="/"
            className={cn(
              "font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105",
              scrolled
                ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            )}
          >
            НАЧАЛО
          </Link>
          <Link
            href="/voting"
            className={cn(
              "font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105",
              scrolled
                ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            )}
          >
            VOTING.BG - ВОУТИНГ.БГ
          </Link>
          <a
            href="#clients"
            className={cn(
              "font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105",
              scrolled
                ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            )}
          >
            НАШИ КЛИЕНТИ
          </a>
          <a
            href="#contact"
            className={cn(
              "font-medium px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105",
              scrolled
                ? "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
            )}
          >
            ЗА КОНТАКТ
          </a>

          {/* User Actions */}
          {session && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <UserCircleIcon className={cn(
                  "w-6 h-6",
                  scrolled ? "text-gray-600" : "text-gray-600"
                )} />
                <span className={cn(
                  "text-sm font-medium",
                  scrolled ? "text-gray-700" : "text-gray-700"
                )}>
                  {session.user?.name || session.user?.email}
                </span>
              </div>
              {session.user?.role !== "USER" && (
                <Link
                  href="/dashboard"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  Админ панел
                </Link>
              )}
              <button
                onClick={() => signOut()}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Изход
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={cn(
              "p-2 rounded-lg transition-colors duration-300",
              scrolled
                ? "text-gray-700 hover:bg-gray-100"
                : "text-gray-700 hover:bg-gray-100"
            )}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100 shadow-lg">
          <div className="px-4 py-6 space-y-4">
            <Link
              href="/"
              className="block text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-300"
              onClick={() => setMenuOpen(false)}
            >
              НАЧАЛО
            </Link>
            <Link
              href="/voting"
              className="block text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-300"
              onClick={() => setMenuOpen(false)}
            >
              VOTING.BG
            </Link>
            <a
              href="#clients"
              className="block text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-300"
              onClick={() => setMenuOpen(false)}
            >
              НАШИ КЛИЕНТИ
            </a>
            <a
              href="#contact"
              className="block text-gray-700 hover:text-blue-600 font-medium py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-300"
              onClick={() => setMenuOpen(false)}
            >
              ЗА КОНТАКТ
            </a>

            <div className="border-t border-gray-200 my-4"></div>
            
            {session && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 px-4">
                  <UserCircleIcon className="w-8 h-8 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">{session.user?.name || session.user?.email}</p>
                    <p className="text-sm text-gray-500">{session.user?.role}</p>
                  </div>
                </div>
                {session.user?.role !== "USER" && (
                  <Link
                    href="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Админ панел
                  </Link>
                )}
                <button
                  onClick={() => { setMenuOpen(false); signOut(); }}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all duration-300"
                >
                  Изход
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}